import { useState, useEffect } from "react";
import { PaziaModal } from "components/molecules";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaButton } from "components/atoms";
import { ColumnWidthInput } from "./ColumnWidthInput";
import { LayoutColumn, ColumnWidth, ColumnMobileConfig, ColumnMargin } from "./types";

interface ColumnSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  column: LayoutColumn | null;
  onSave: (width: ColumnWidth, mobileConfig: ColumnMobileConfig, margin: ColumnMargin) => void;
}

const MarginBreakpointInputs = ({
  label,
  left,
  right,
  onLeftChange,
  onRightChange,
}: {
  label: string;
  left: number | undefined;
  right: number | undefined;
  onLeftChange: (v: number | undefined) => void;
  onRightChange: (v: number | undefined) => void;
}) => (
  <div className="flex items-center gap-3">
    <span className="w-16 text-xs text-slate-500 dark:text-slate-400">{label}</span>
    <div className="flex items-center gap-1">
      <span className="text-xs text-slate-400">L</span>
      <input
        type="number"
        min={0}
        value={left ?? ""}
        onChange={(e) => onLeftChange(e.target.value === "" ? undefined : Number(e.target.value))}
        placeholder="0"
        className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
      />
    </div>
    <div className="flex items-center gap-1">
      <span className="text-xs text-slate-400">R</span>
      <input
        type="number"
        min={0}
        value={right ?? ""}
        onChange={(e) => onRightChange(e.target.value === "" ? undefined : Number(e.target.value))}
        placeholder="0"
        className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
      />
    </div>
  </div>
);

export const ColumnSettingsModal = ({ isOpen, onClose, column, onSave }: ColumnSettingsModalProps) => {
  const [width, setWidth] = useState<ColumnWidth>({ value: 1, unit: "fr" });
  const [mobileVisible, setMobileVisible] = useState(true);
  const [mobileOrder, setMobileOrder] = useState<number | undefined>(undefined);
  const [margin, setMargin] = useState<ColumnMargin>({});

  useEffect(() => {
    if (column) {
      setWidth(column.width);
      setMobileVisible(column.mobileConfig.visible);
      setMobileOrder(column.mobileConfig.order);
      setMargin(column.margin ?? {});
    }
  }, [column]);

  const updateMarginBreakpoint = (
    bp: "default" | "md" | "lg" | "xl",
    side: "left" | "right",
    value: number | undefined,
  ) => {
    setMargin((prev) => ({
      ...prev,
      [bp]: { ...prev[bp], [side]: value },
    }));
  };

  const handleSave = () => {
    onSave(width, { visible: mobileVisible, order: mobileOrder }, margin);
    onClose();
  };

  return (
    <PaziaModal id="column-settings-modal" onCloseRequest={onClose} isOpen={isOpen} size="md">
      {isOpen && column && (
        <ModalPadding>
          <ModalHeader>Ustawienia kolumny</ModalHeader>
          <div className="mt-4 space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Szerokość
              </label>
              <ColumnWidthInput value={width} onChange={setWidth} />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Widoczność na mobile
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={mobileVisible}
                  onChange={(e) => setMobileVisible(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Widoczna na urządzeniach mobilnych
                </span>
              </label>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Kolejność na mobile (opcjonalnie)
              </label>
              <input
                type="number"
                min={0}
                value={mobileOrder ?? ""}
                onChange={(e) =>
                  setMobileOrder(e.target.value === "" ? undefined : Number(e.target.value))
                }
                placeholder="Domyślna"
                className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              />
              <p className="mt-1 text-xs text-slate-400">
                Niższa wartość = wyżej na mobile. Puste = domyślna kolejność.
              </p>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Marginesy (px)
              </label>
              <div className="space-y-2">
                <MarginBreakpointInputs
                  label="Domyślny"
                  left={margin.default?.left}
                  right={margin.default?.right}
                  onLeftChange={(v) => updateMarginBreakpoint("default", "left", v)}
                  onRightChange={(v) => updateMarginBreakpoint("default", "right", v)}
                />
                <MarginBreakpointInputs
                  label="md (768+)"
                  left={margin.md?.left}
                  right={margin.md?.right}
                  onLeftChange={(v) => updateMarginBreakpoint("md", "left", v)}
                  onRightChange={(v) => updateMarginBreakpoint("md", "right", v)}
                />
                <MarginBreakpointInputs
                  label="lg (1024+)"
                  left={margin.lg?.left}
                  right={margin.lg?.right}
                  onLeftChange={(v) => updateMarginBreakpoint("lg", "left", v)}
                  onRightChange={(v) => updateMarginBreakpoint("lg", "right", v)}
                />
                <MarginBreakpointInputs
                  label="xl (1280+)"
                  left={margin.xl?.left}
                  right={margin.xl?.right}
                  onLeftChange={(v) => updateMarginBreakpoint("xl", "left", v)}
                  onRightChange={(v) => updateMarginBreakpoint("xl", "right", v)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Anuluj
              </button>
              <PaziaButton type="success" title="Zapisz" onClick={handleSave} />
            </div>
          </div>
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
